import Link from 'next/link';
import { Router } from 'next/router';
import { NewsletterApi as NewsletterApiClient } from 'pleinchamp-api-client';
import React, { ComponentPropsWithRef, FC, useCallback, useEffect } from 'react';
import { Trans } from 'react-i18next';
import { ApiConfiguration, axiosInstance } from '@api/business/api.conf';
import { PrimaryButton } from '@components/buttons/FlatButton/FlatButton.component';
import { Text } from '@components/Text/Text.component';
import { formValidator } from '@form/business/form.types';
import { FormValidators, useFormState } from '@form/business/useFormState.hook';
import { FormHints } from '@form/components/FormHints/FormHints.component';
import { Input } from '@form/components/Input/Input.component';
import { PlcForm } from '@form/components/PlcForm/PlcForm.component';
import { EmailIcon } from '@icons/Email.icon';
import { useTranslation } from '@locales/useTranslation.hook';
import { Breakpoint, useClassNames, useIsBreakpointDown } from '@styles/breakpoints';
import { PlcColor } from '@styles/colors';
import { urlEnum } from '@utils/url';
import classnames from 'classnames';
import styles from './Footer.module.scss';

export interface NewsletterRegistrationProps extends ComponentPropsWithRef<'div'> {
  close?: () => void;
}

interface FormState {
  email: string;
}

const initialFormState: FormState = {
  email: '',
};

const NewsletterApi = new NewsletterApiClient(ApiConfiguration, undefined, axiosInstance);

const NewsletterRegistration: FC<NewsletterRegistrationProps> = ({ close }) => {
  const { t, ready } = useTranslation(['common', 'auth']);
  const isSmartphone = useIsBreakpointDown(Breakpoint.s);
  const validators: FormValidators<FormState> = {
    email: {
      method: (email) => [formValidator(email, t, true).email()],
      ref: undefined,
    },
  };

  const {
    formData,
    disabledSubmitButton,
    setFormField,
    plcFormProps,
    isSubmitted,
    submitCallbackErrorCode,
    submitCallbackSuccess,
    resetForm,
  } = useFormState({
    initialFormState,
    validators,
  });

  const { email } = formData;

  function onSubmitCallback() {
    const result = NewsletterApi.newsletterSubscribe({ email });
    result.then(() => {
      if (close) close();
    });
    return result;
  }

  const formClassName = useClassNames({
    all: [styles['plc-footer-form'], 'flex', 'column', 'wrap', 'plc-mt-m', 'plc-mb-basis'],
    m: ['plc-mb-0'],
    s: ['row', 'nowrap', 'plc-mr-m'],
  });
  const resetFormOnBlur = useCallback(() => resetForm(), [resetForm]);

  useEffect(() => {
    Router.events.on('routeChangeComplete', resetFormOnBlur);

    return () => {
      Router.events.off('routeChangeComplete', resetFormOnBlur);
    };
  }, []);

  if (ready) {
    return (
      <>
        <Text i18nKey="common.footer.signup-title" tag="span" variant="h4" />
        <Text className="plc-mt-s" variant="tiny">
          <Trans i18nKey="common.footer.information-mention" />
        </Text>
        <PlcForm className={formClassName} onSubmitCallback={onSubmitCallback} withLoader={false} {...plcFormProps}>
          <Input
            icon={<EmailIcon className={styles['plc-email']} fillColor={PlcColor.GREEN} />}
            isHintVisible={isSubmitted}
            isRightIcon={false}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => setFormField('email', event.target.value)}
            onFocus={() => {
              if (isSubmitted && (submitCallbackErrorCode || submitCallbackSuccess)) {
                resetForm();
              }
            }}
            placeholderI18n="common.footer.signup-placeholder"
            type="email"
            validators={validators.email?.method(email)}
            value={email}
            variant="full-width"
          />
          <PrimaryButton
            className={classnames(isSubmitted ? 'plc-mt-m' : 'plc-mt-s', 'plc-mt-up-s-0', 'plc-ml-up-s-s')}
            disabled={disabledSubmitButton}
            isDarkGreen
            textProps={{ i18nKey: 'common.footer.signup-submit' }}
            type="submit"
          />
          <FormHints
            className={styles['form-hints']}
            isFooter={true}
            isHintVisible={isSubmitted}
            validators={[
              {
                isValid: !submitCallbackSuccess,
                message: t('common.footer.newsletter-form.success'),
                type: 'success',
              },
              {
                isValid: !submitCallbackErrorCode,
                message: t('common.error.generic'),
                type: 'error',
              },
            ]}
          />
        </PlcForm>
        <Text className={isSmartphone ? 'plc-mb-basis' : 'plc-mt-m'} variant="tiny">
          <Trans className="plc-mt-m" i18nKey="common.footer.signup-mention" />
          <br />
          <Link href={urlEnum.privacyPolicy}>En savoir plus sur la gestion de vos données et vos droits</Link>
        </Text>
      </>
    );
  }
  return <></>;
};

export { NewsletterRegistration };
